import { Injectable, inject } from '@angular/core';
import {
  BiometricAuth,
  BiometryType,
  type CheckBiometryResult,
} from '@aparajita/capacitor-biometric-auth';
import { SecureStorageService } from './secure-storage.service';
import { hasNativePlugin } from './platform';

/** Registered as 'BiometricAuthNative' in the plugin's index.js */
const PLUGIN_NAME = 'BiometricAuthNative';
const BIOMETRIC_ENABLED_KEY = 'zyn_biometric_enabled';

const UNAVAILABLE_RESULT: CheckBiometryResult = {
  isAvailable: false,
  strongBiometryIsAvailable: false,
  biometryType: BiometryType.none,
  biometryTypes: [],
  deviceIsSecure: false,
  reason: 'Plugin unavailable',
  code: '' as any,
};

@Injectable({ providedIn: 'root' })
export class BiometricService {
  readonly #secureStorage = inject(SecureStorageService);
  readonly #useNative = hasNativePlugin(PLUGIN_NAME);

  /** Check if the device supports biometric authentication. */
  async checkBiometry(): Promise<CheckBiometryResult> {
    if (!this.#useNative) {
      return UNAVAILABLE_RESULT;
    }
    try {
      return await BiometricAuth.checkBiometry();
    } catch {
      return UNAVAILABLE_RESULT;
    }
  }

  /** Whether Face ID / Touch ID / fingerprint is available on this device. */
  async isAvailable(): Promise<boolean> {
    const result = await this.checkBiometry();
    return result.isAvailable;
  }

  /**
   * Prompt the user for biometric authentication.
   * Returns true on success, false on failure/cancellation.
   * Does NOT destroy the session on failure.
   */
  async authenticate(reason = 'Unlock zynday'): Promise<boolean> {
    if (!this.#useNative) {
      return false;
    }
    try {
      await BiometricAuth.authenticate({
        reason,
        allowDeviceCredential: true,
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Whether the user has opted in to biometric unlock. */
  async isEnabled(): Promise<boolean> {
    const value = await this.#secureStorage.get(BIOMETRIC_ENABLED_KEY);
    return value === 'true';
  }

  /** Persist the user's biometric opt-in/out preference. */
  async setEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      await this.#secureStorage.set(BIOMETRIC_ENABLED_KEY, 'true');
    } else {
      await this.#secureStorage.remove(BIOMETRIC_ENABLED_KEY);
    }
  }
}
